import { addSectionsToReel } from './addSectionsToReel';

import type { AddedReelSection } from './addSectionsToReel';
import type { Reel } from './reelTypes';

export type ReelSectionReplacement = {
  /** The Section that left the reel. */
  removedSectionId: string;
  /**
   * The Sections that took its place, in playback order — one for an ordinary
   * clip, one per passage for a clip that is itself a reel (DE15). Each carries
   * the incoming clip's title, which seeds its name (DE8).
   */
  added: readonly AddedReelSection[];
};

/**
 * PLAT-11071 — the stored half of a Section swap (DE13).
 *
 * The incoming Sections are named exactly as an insert names them, through
 * `addSectionsToReel` — seeded from the clip's title with `nameSource: 'clip'`,
 * nothing stored when it has none. What a *swap* adds on top is two things:
 *
 * **A `'user'` name survives; a `'clip'` or `'generated'` one does not** (DS3). A
 * creator who typed "Opening hook" named the *slot* rather than the passage, and
 * DE10's rule is that a typed name is never overwritten — so it moves onto the
 * first arriving Section, which starts where the departing one did. A seeded or
 * generated name described the passage that just left, so the incoming title
 * replaces it and PLAT-11059's ↻ can regenerate from there. Either way the
 * departing Section's own entry goes.
 *
 * **Chapters are positional (DE12), so a same-position swap needs no membership
 * write** — `deriveReelChapterGroups` re-reads it off the row order. The one
 * anchor that does move is a chapter whose `firstSectionId` was the departing
 * Section: repointed at the first arriving one, so the chapter opens on the
 * replacement rather than sliding forward onto the Section after it.
 *
 * Pure and non-mutating — the caller writes the returned reel into
 * `clipJson.reel`.
 */
export function replaceSectionInReel(
  reel: Reel | undefined,
  { removedSectionId, added }: ReelSectionReplacement,
): Reel {
  const { sections } = addSectionsToReel(reel, added);

  const departing = sections[removedSectionId];
  delete sections[removedSectionId];

  const opening = added[0]?.sectionId;
  if (opening && departing?.nameSource === 'user') {
    sections[opening] = departing;
  }

  return {
    // `...reel` first, so anything else stored on it rides across rather than
    // being dropped by a writer that predates it (PLAT-11072
    // `disabledSections`).
    ...reel,
    chapters: (reel?.chapters ?? []).map(chapter =>
      opening && chapter.firstSectionId === removedSectionId
        ? {
            ...chapter,
            firstSectionId: opening,
            afterSectionId: undefined,
          }
        : chapter,
    ),
    sections,
  };
}
